/**
 * ConfirmDialog — Reusable confirmation dialog for destructive actions.
 * Use before deleting exercises, cancelling duels, etc.
 */
import { Modal, View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';

interface Props {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  visible, title, message,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  destructive = true,
  onConfirm, onCancel,
}: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={[styles.box, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.title, { color: c.text }]}>{title}</Text>
          <Text style={[styles.message, { color: c.textSecondary }]}>{message}</Text>
          <View style={styles.buttons}>
            <TouchableOpacity
              style={[styles.btn, { backgroundColor: c.surfaceVariant }]}
              onPress={onCancel}
              activeOpacity={0.7}
            >
              <Text style={[styles.btnText, { color: c.text }]}>{cancelLabel}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btn, { backgroundColor: destructive ? '#8B1A1A' : c.accent }]}
              onPress={onConfirm}
              activeOpacity={0.7}
            >
              <Text style={[styles.btnText, { color: '#E8E4DB' }]}>{confirmLabel}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(3,3,3,0.85)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  box: { width: '100%', maxWidth: 360, borderWidth: 1, borderRadius: RADIUS.xs, padding: SPACE.lg, gap: SPACE.md },
  title: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold, textTransform: 'uppercase' },
  message: { fontSize: FONT.sm, fontFamily: TYPO.body.regular, lineHeight: 20 },
  buttons: { flexDirection: 'row', gap: SPACE.sm, marginTop: SPACE.sm },
  btn: { flex: 1, paddingVertical: 14, alignItems: 'center', borderRadius: RADIUS.none },
  btnText: { fontSize: FONT.sm, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase', letterSpacing: 1 },
});
